import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { KieneTableService } from '../../../../components/kiene-table/kiene-table.service';
import { MessageService } from '../../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { TierwohlprotokollRind } from '../classes/tierwohlprotokoll-rind';
import { Tierarzt } from '../../../../../../projects/kiene-core/src/lib/components/tieraerzte/classes/tierarzt';
import { API_BASE_URL_SERVICE } from '../../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { Client } from '../../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { FormControl } from '@angular/forms';
import { applyParsedTierwohlDates, withFormattedTierwohlDates } from '../../twp-date';

/** 0/1-Flags aus der API oft als String "0"/"1" – fuer Radios/Checkboxen normalisieren. */
const TWP_RIND_FLAG_FIELDS: (keyof TierwohlprotokollRind)[] = [
    'gesundheitsstatus_bhv1',
    'gesundheitsstatus_bvd',
    'bestand_klinisch_ohne_befund',
    'hinweise_tierseuchen',
    'mangagementfaktor_haltungssystem',
    'managementfaktor_fuetterung_wasserversorgung',
    'managementfaktor_betrieb',
    'managementfaktor_hygiene',
    'managementfaktor_zucht',
    'managementfaktor_sonstiges',
    'gesundheitsstatus_atmungsorgane',
    'gesundheitsstatus_verdauungsapparat',
    'gesundheitsstatus_bewegungsapparat',
    'gesundheitsstatus_haut_kondition',
    'gesundheitsstatus_eutergesundheit',
    'gesundheitsstatus_sonstiges',
    'diagnostische_massnahmen_sektion',
    'diagnostische_massnahmen_kot',
    'diagnostische_massnahmen_resistenztest',
    'diagnostische_massnahmen_futter_wasser',
    'diagnostische_massnahmen_blut',
    'diagnostische_massnahmen_hautgeschabsel',
    'diagnostische_massnahmen_milch',
    'diagnostische_massnahmen_tupfer',
    'diagnostische_massnahmen_sonstiges',
    'antibiotika_monitoring',
    'betriebliche_eigenkontrolle',
    'tierschutzindikatoren',
    'milchleistungspruefung',
    'rationsberechnung',
    'traenkwasser_qualitaet',
    'stallklima_kontrolliert',
    'impfprogramm_bvd',
    'impfprogramm_mutterschutz',
    'impfprogramm_bhv1',
    'impfprogramm_blauzunge',
    'impfprogramm_brsv_pasteurellen',
    'impfprogramm_sonstiges',
    'parasitologische_massnahmen_endoparasiten',
    'parasitologische_massnahmen_ektoparasiten',
    'plan_tiergesundheit_hygienemanagement',
    'massnahmenplan_einzelaktivitaeten',
    'abrechnen',
];

@Component({
    selector: 'kiene-tierwohlprotokoll-rind-details',
    templateUrl: './tierwohlprotokoll-rind-details.component.html',
    styleUrls: ['./tierwohlprotokoll-rind-details.component.scss'],
})
export class TierwohlprotokollRindDetailsComponent implements OnDestroy {
    protected readonly KieneUtils = KieneUtils;

    tierwohlprotokollRind: TierwohlprotokollRind;
    selectedKunde: Client;
    selectedTierarzt: Tierarzt;
    userIstTierarzt = false;
    abrechnenCtrl = new FormControl(true);
    private autosaveHandle?: number;
    private isSavingDraft = false;
    private autosaveReady = false;

    constructor(
        public dialogRef: MatDialogRef<TierwohlprotokollRindDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) private data,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private api: KieneTableService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
    ) {
        if (data?.tierwohlprotokollRind) {
            this.tierwohlprotokollRind = data?.tierwohlprotokollRind;
            this.normalizeApiFlags(this.tierwohlprotokollRind);
            applyParsedTierwohlDates(this.tierwohlprotokollRind);
            this.generateObjsForAutocompletes();
        } else {
            this.tierwohlprotokollRind = new TierwohlprotokollRind();
            this.tierwohlprotokollRind.status_id = 153;
        }

        if (KieneUtils.isApiZero(this.tierwohlprotokollRind.abrechnen)) {
            this.abrechnenCtrl.setValue(false);
        } else if (KieneUtils.isApiOne(this.tierwohlprotokollRind.abrechnen)) {
            this.abrechnenCtrl.setValue(true);
        }
        if (this.tierwohlprotokollRind.abgabebeleg_id) {
            this.abrechnenCtrl.disable();
        }

        if (!this.tierwohlprotokollRind.datum) {
            this.tierwohlprotokollRind.datum = new Date();
        }

        if (localStorageService.getCurrentClient()) {
            this.selectedKunde = localStorageService.getCurrentClient();
            this.setKunde(this.selectedKunde);
        }

        this.userIstTierarzt = localStorageService.isCurrentUserVeterinarian();
        if (this.userIstTierarzt) {
            const session = localStorageService.getCurrentUser();
            this.selectedTierarzt = new Tierarzt();
            this.selectedTierarzt.tierarzt_id = session.tierarzt_id;
            this.selectedTierarzt.vorname = session.vorname;
            this.selectedTierarzt.nachname = session.nachname;
            this.setTierarzt(this.selectedTierarzt);
        }
        this.autosaveReady = true;
    }

    ngOnDestroy(): void {
        this.clearAutosaveTimer();
    }

    @HostListener('input')
    @HostListener('change')
    onAutosaveInput(): void {
        this.scheduleAutosave();
    }

    save() {
        this.clearAutosaveTimer();
        this.persist(false);
    }

    private scheduleAutosave(): void {
        if (!this.autosaveReady || KieneUtils.apiStatusId(this.tierwohlprotokollRind?.status_id, 154)) {
            return;
        }
        this.clearAutosaveTimer();
        this.autosaveHandle = window.setTimeout(() => this.persist(true), 1500);
    }

    private clearAutosaveTimer(): void {
        if (this.autosaveHandle) {
            window.clearTimeout(this.autosaveHandle);
            this.autosaveHandle = undefined;
        }
    }

    private persist(silent: boolean): void {
        if (KieneUtils.apiStatusId(this.tierwohlprotokollRind?.status_id, 154)) {
            return;
        }
        if (this.isSavingDraft) {
            if (silent) {
                this.scheduleAutosave();
            }
            return;
        }
        this.normalizeNullableDates(this.tierwohlprotokollRind);
        this.normalizeNullableAnzahlen(this.tierwohlprotokollRind);
        this.tierwohlprotokollRind.abrechnen = this.abrechnenCtrl.value ? 1 : 0;
        if (!this.tierwohlprotokollRind.status_id) {
            this.tierwohlprotokollRind.status_id = 153;
        }
        if (!this.tierwohlprotokollRind.kunde_id) {
            if (!silent) {
                this.messageService.alertMessage('Bitte einen Betrieb auswählen!');
            }
            return;
        }
        this.isSavingDraft = true;
        const isCreate = !this.tierwohlprotokollRind.twp_rind_id;
        const onOk = (response?: TierwohlprotokollRind) => {
            this.isSavingDraft = false;
            if (response?.twp_rind_id) {
                this.tierwohlprotokollRind.twp_rind_id = response.twp_rind_id;
            }
            if (!silent) {
                this.messageService.infoMessage(
                    isCreate
                        ? 'Tierwohlprotokoll wurde erfolgreich erstellt.'
                        : 'Tierwohlprotokoll wurde erfolgreich aktualisiert.',
                );
                this.dialogRef.close(true);
            }
        };
        const onErr = (error: any) => {
            this.isSavingDraft = false;
            if (!silent) {
                this.messageService.errorMessage('Fehler beim Speichern des Tierwohlprotokolls!', error);
            }
        };
        const payload = withFormattedTierwohlDates(this.tierwohlprotokollRind);
        if (this.tierwohlprotokollRind.twp_rind_id) {
            this.api
                .updateEntity(this.apiBaseUrl + 'tierwohlprotokoll_rind/update.php', payload)
                .subscribe({ next: onOk, error: onErr });
        } else {
            this.api
                .post(this.apiBaseUrl + 'tierwohlprotokoll_rind/create.php', payload)
                .subscribe({ next: onOk, error: onErr });
        }
    }

    private normalizeNullableDates(twp: TierwohlprotokollRind) {
        const dateKeys: (keyof TierwohlprotokollRind)[] = [
            'datum',
            'durchfuehrung_bis',
            'naechster_bestandsbesuch',
            'parasitologische_massnahmen_endoparasiten_zuletzt',
            'parasitologische_massnahmen_ektoparasiten_zuletzt',
        ];
        for (const key of dateKeys) {
            const value = twp[key] as unknown;
            if (value == null || value === '' || (value instanceof Date && Number.isNaN(value.getTime()))) {
                (twp as any)[key] = null;
            }
        }
    }

    /** Leere Anzahl-Felder explizit als null senden, sonst bleibt der alte Wert in der DB. */
    private normalizeNullableAnzahlen(twp: TierwohlprotokollRind): void {
        const keys: (keyof TierwohlprotokollRind)[] = [
            'anzahl_milchkuehe',
            'anzahl_faersen',
            'anzahl_kaelber',
            'anzahl_mastrinder',
            'anzahl_mastkaelber',
            'biologisch_tageszunahmen_masttiere',
            'biologisch_nutzungsdauer_milchvieh',
        ];
        for (const key of keys) {
            const value = twp[key] as unknown;
            if (value === '' || value === undefined) {
                (twp as any)[key] = null;
            }
        }
    }

    setKunde(c: Client) {
        this.tierwohlprotokollRind.kunde_id = c.kunde_id;
        this.tierwohlprotokollRind.kunde = c.bezeichnung_final;
        this.tierwohlprotokollRind.kunde_vvvo = c.vvvo;
        this.tierwohlprotokollRind.kunde_strasse = c.strasse_final;
        this.tierwohlprotokollRind.kunde_plz = c.plz_final;
        this.tierwohlprotokollRind.kunde_ort = c.ort_final;
        this.scheduleAutosave();
    }

    setTierarzt(t: Tierarzt) {
        this.tierwohlprotokollRind.tierarzt_id = t.tierarzt_id;
        this.tierwohlprotokollRind.tierarzt_vorname = t.vorname;
        this.tierwohlprotokollRind.tierarzt_nachname = t.nachname;
        this.scheduleAutosave();
    }

    private normalizeApiFlags(twp: TierwohlprotokollRind) {
        for (const key of TWP_RIND_FLAG_FIELDS) {
            const value = twp[key];
            if (value == null || value === ('' as never)) {
                (twp as any)[key] = null;
            } else if (KieneUtils.isApiOne(value)) {
                (twp as any)[key] = 1;
            } else if (KieneUtils.isApiZero(value)) {
                (twp as any)[key] = 0;
            }
        }
        if (twp.status_id != null && twp.status_id !== ('' as never)) {
            twp.status_id = Number(twp.status_id);
        }
    }

    private generateObjsForAutocompletes() {
        if (this.tierwohlprotokollRind?.kunde_id) {
            this.loadKunde(this.tierwohlprotokollRind?.kunde_id).subscribe({
                next: value => {
                    this.selectedKunde = value;
                },
                error: () => {
                    this.selectedKunde = new Client();
                    this.selectedKunde.kunde_id = this.tierwohlprotokollRind.kunde_id;
                    this.selectedKunde.bezeichnung_final = this.tierwohlprotokollRind.kunde;
                    this.selectedKunde.vvvo = this.tierwohlprotokollRind.kunde_vvvo;
                    this.selectedKunde.strasse_final = this.tierwohlprotokollRind.kunde_strasse;
                    this.selectedKunde.plz_final = this.tierwohlprotokollRind.kunde_plz;
                    this.selectedKunde.ort_final = this.tierwohlprotokollRind.kunde_ort;
                },
            });
        }

        if (this.tierwohlprotokollRind?.tierarzt_id) {
            this.loadTierarzt(this.tierwohlprotokollRind.tierarzt_id).subscribe({
                next: value => {
                    this.selectedTierarzt = value;
                },
                error: () => {
                    this.selectedTierarzt = new Tierarzt();
                    this.selectedTierarzt.tierarzt_id = this.tierwohlprotokollRind.tierarzt_id;
                    this.selectedTierarzt.vorname = this.tierwohlprotokollRind.tierarzt_vorname;
                    this.selectedTierarzt.nachname = this.tierwohlprotokollRind.tierarzt_nachname;
                },
            });
        }
    }

    private loadKunde(kunde_id: number) {
        return this.api.getOne(this.apiBaseUrl + 'kunde/read_one.php', 'kunde_id', kunde_id.toString());
    }

    private loadTierarzt(tierarzt_id: number) {
        return this.api.getOne(this.apiBaseUrl + 'tierarzt/read_one.php', 'tierarzt_id', tierarzt_id.toString());
    }
}
